/**
 * RETOBLOCK COMPILER: SCRATCH BLOCKS AST -> PYTHON (retologix SDK)
 * Tuân thủ tuyệt đối đặc tả trong "TÀI LIỆU MÔ TẢ KỸ THUẬT CHUYỂN ĐỔI KHỐI LỆNH SANG PYTHON"
 */

export class BlockCompiler {
  constructor() {
    this.indentStep = '    ';
  }

  /**
   * Biên dịch toàn bộ cây khối lệnh blocks.json thành mã nguồn Python hoàn chỉnh
   */
  compile(blocksData, projectName = 'Du_An_Robot') {
    const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 19);

    let pythonBody = '';
    const rootBlock = this.findRootBlock(blocksData);

    if (rootBlock) {
      pythonBody = this.compileBlockChain(rootBlock.next, blocksData, 1);
    }

    if (!pythonBody.trim()) {
      pythonBody = `${this.indentStep}pass\n`;
    }

    const fullScript = `# ==============================================================================
# RETOLOGIX STUDIO - AUTO-GENERATED RUNTIME SCRIPT
# Project: ${projectName}
# Target Kit: RETOCAR-STARTER (ESP32)
# Generated at: ${timestamp}
# ==============================================================================
from retologix import RetoCar
import time
import urandom

# Khoi tao doi tuong dieu khien robot RETOCAR-STARTER
robot = RetoCar()

try:
    # --- NOI DUNG KICH BAN THUC THI DICH TU KHOI LENH ---
${pythonBody}
    # ----------------------------------------------------
except KeyboardInterrupt:
    # Co che ngat tuc thoi khi nhan lenh STOP tu RETOBLOCK
    robot.stop()
    robot.buzzer_off()
`;

    return fullScript;
  }

  findRootBlock(blocksData) {
    const blocks = blocksData.blocks || blocksData;
    for (const id in blocks) {
      const block = blocks[id];
      if (block.opcode === 'event_whenflagclicked' || block.opcode === 'when_program_starts') {
        return block;
      }
    }
    return null;
  }

  compileBlockChain(firstBlockId, blocksData, indentLevel) {
    const blocks = blocksData.blocks || blocksData;
    let code = '';
    let currentId = firstBlockId;

    while (currentId) {
      const block = blocks[currentId];
      if (!block) break;

      const blockCode = this.compileSingleBlock(block, blocksData, indentLevel);
      if (blockCode) {
        code += blockCode;
      }

      currentId = block.next;
    }

    return code;
  }

  compileSingleBlock(block, blocksData, indentLevel) {
    const indent = this.indentStep.repeat(indentLevel);
    const opcode = block.opcode;
    const inputs = block.inputs || {};
    const fields = block.fields || {};

    switch (opcode) {
      // 1. Control & Events
      case 'control_forever': {
        const substack = inputs.SUBSTACK?.block;
        const bodyCode = substack
          ? this.compileBlockChain(substack, blocksData, indentLevel + 1)
          : `${this.indentStep.repeat(indentLevel + 1)}pass\n`;

        return `${indent}while True:\n${bodyCode}${this.indentStep.repeat(indentLevel + 1)}time.sleep(0.001)\n`;
      }

      case 'control_repeat': {
        const times = this.resolveInputValue(inputs.TIMES, blocksData, 10);
        const substack = inputs.SUBSTACK?.block;
        const bodyCode = substack
          ? this.compileBlockChain(substack, blocksData, indentLevel + 1)
          : `${this.indentStep.repeat(indentLevel + 1)}pass\n`;

        return `${indent}for _ in range(${times}):\n${bodyCode}`;
      }

      case 'control_if': {
        const cond = this.resolveBooleanValue(inputs.CONDITION, blocksData, 'True');
        const substack = inputs.SUBSTACK?.block;
        const bodyCode = substack
          ? this.compileBlockChain(substack, blocksData, indentLevel + 1)
          : `${this.indentStep.repeat(indentLevel + 1)}pass\n`;

        return `${indent}if ${cond}:\n${bodyCode}`;
      }

      case 'control_if_else': {
        const cond = this.resolveBooleanValue(inputs.CONDITION, blocksData, 'True');
        const substack1 = inputs.SUBSTACK?.block;
        const substack2 = inputs.SUBSTACK2?.block;

        const body1 = substack1
          ? this.compileBlockChain(substack1, blocksData, indentLevel + 1)
          : `${this.indentStep.repeat(indentLevel + 1)}pass\n`;

        const body2 = substack2
          ? this.compileBlockChain(substack2, blocksData, indentLevel + 1)
          : `${this.indentStep.repeat(indentLevel + 1)}pass\n`;

        return `${indent}if ${cond}:\n${body1}${indent}else:\n${body2}`;
      }

      case 'control_wait': {
        const sec = this.resolveInputValue(inputs.SECONDS, blocksData, 1);
        return `${indent}time.sleep(${sec})\n`;
      }

      case 'control_wait_until': {
        const cond = this.resolveBooleanValue(inputs.CONDITION, blocksData, 'True');
        return `${indent}while not (${cond}):\n${this.indentStep.repeat(indentLevel + 1)}time.sleep(0.01)\n`;
      }

      // 2. Movement
      case 'reto_movement_direction_speed': {
        const dir = fields.DIRECTION || inputs.DIRECTION?.value || 'FORWARD';
        const speed = this.resolveInputValue(inputs.SPEED, blocksData, 60);
        return `${indent}robot.move(direction="${dir}", speed=${speed})\n`;
      }

      case 'reto_movement_stop': {
        return `${indent}robot.stop()\n`;
      }

      case 'reto_movement_motor_single': {
        const motor = fields.MOTOR || inputs.MOTOR?.value || 'LEFT';
        const dir = fields.DIRECTION || inputs.DIRECTION?.value || 'FORWARD';
        const speed = this.resolveInputValue(inputs.SPEED, blocksData, 50);
        return `${indent}robot.set_motor(motor="${motor}", direction="${dir}", speed=${speed})\n`;
      }

      // 3. Actuators
      case 'reto_actuator_set_arm_angle': {
        const angle = this.resolveInputValue(inputs.ANGLE, blocksData, 90);
        return `${indent}robot.set_arm_angle(angle=${angle})\n`;
      }

      // 4. Feedback
      case 'reto_feedback_set_rgb': {
        const color = fields.COLOR || inputs.COLOR?.value || 'RED';
        return `${indent}robot.set_rgb_color(color="${color}")\n`;
      }

      case 'reto_feedback_set_indicator': {
        const side = fields.SIDE || inputs.SIDE?.value || 'LEFT';
        const stateStr = fields.STATE || inputs.STATE?.value || 'TRUE';
        const state = stateStr.toUpperCase() === 'TRUE' ? 'True' : 'False';
        return `${indent}robot.set_indicator(side="${side}", state=${state})\n`;
      }

      case 'reto_feedback_buzzer_tone': {
        const freq = this.resolveInputValue(inputs.FREQ, blocksData, 1000);
        return `${indent}robot.buzzer_tone(${freq})\n`;
      }

      case 'reto_feedback_buzzer_off': {
        return `${indent}robot.buzzer_off()\n`;
      }

      default:
        return '';
    }
  }

  resolveInputValue(inputSlot, blocksData, fallback) {
    if (!inputSlot) return fallback;
    if (inputSlot.value !== undefined) return inputSlot.value;
    if (inputSlot.block) {
      const blocks = blocksData.blocks || blocksData;
      const b = blocks[inputSlot.block];
      if (b) return this.compileExpressionBlock(b, blocksData);
    }
    return fallback;
  }

  resolveBooleanValue(inputSlot, blocksData, fallback = 'True') {
    if (!inputSlot) return fallback;
    if (inputSlot.block) {
      const blocks = blocksData.blocks || blocksData;
      const b = blocks[inputSlot.block];
      if (b) return this.compileBooleanBlock(b, blocksData);
    }
    return fallback;
  }

  compileExpressionBlock(block, blocksData) {
    const opcode = block.opcode;
    const inputs = block.inputs || {};
    const fields = block.fields || {};

    switch (opcode) {
      case 'reto_sensor_read_distance':
        return 'robot.get_distance()';

      case 'reto_sensor_read_light':
        return 'robot.get_light_level()';

      case 'reto_sensor_read_color':
        return 'robot.get_color_name()';

      case 'operator_arithmetic': {
        const num1 = this.resolveInputValue(inputs.NUM1, blocksData, 0);
        const op = fields.OP || inputs.OP?.value || '+';
        const num2 = this.resolveInputValue(inputs.NUM2, blocksData, 0);
        return `(${num1} ${op} ${num2})`;
      }

      case 'operator_random': {
        const from = this.resolveInputValue(inputs.FROM, blocksData, 1);
        const to = this.resolveInputValue(inputs.TO, blocksData, 10);
        return `urandom.randint(${from}, ${to})`;
      }

      default:
        return '0';
    }
  }

  compileBooleanBlock(block, blocksData) {
    const opcode = block.opcode;
    const inputs = block.inputs || {};
    const fields = block.fields || {};

    switch (opcode) {
      case 'reto_sensor_read_line': {
        const sensor = fields.SENSOR || inputs.SENSOR?.value || '2';
        const lineColor = fields.LINE_COLOR || inputs.LINE_COLOR?.value || 'BLACK';
        const expr = `robot.get_line(${sensor})`;
        return lineColor === 'WHITE' ? `not (${expr})` : expr;
      }

      case 'reto_sensor_button_pressed': {
        const btn = fields.BUTTON || inputs.BUTTON?.value || '1';
        const state = fields.STATE || fields.ACTION || inputs.STATE?.value || 'PRESSED';
        const expr = `robot.is_button_pressed(${btn})`;
        return state === 'RELEASED' ? `not (${expr})` : expr;
      }

      case 'reto_sensor_color_is': {
        const color = fields.COLOR || inputs.COLOR?.value || 'RED';
        return `(robot.get_color_name() == "${color}")`;
      }

      case 'operator_compare': {
        const op1 = this.resolveInputValue(inputs.OPERAND1, blocksData, 0);
        let op = fields.OP || inputs.OP?.value || '<';
        if (op === '=') op = '==';
        const op2 = this.resolveInputValue(inputs.OPERAND2, blocksData, 0);
        return `(${op1} ${op} ${op2})`;
      }

      case 'operator_logic': {
        const cond1 = this.resolveBooleanValue(inputs.COND1, blocksData, 'True');
        const op = fields.OP || inputs.OP?.value || 'and';
        const cond2 = this.resolveBooleanValue(inputs.COND2, blocksData, 'True');
        return `(${cond1} ${op} ${cond2})`;
      }

      case 'operator_not': {
        const cond = this.resolveBooleanValue(inputs.COND, blocksData, 'False');
        return `not (${cond})`;
      }

      default:
        return 'True';
    }
  }
}
